import React from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import ROUTES from 'routes';
import { useAppDispatch, useAppSelector } from 'hooks';
import { isEmailVerified, selectAccount, selectAccountSettings } from 'redux/account/selectors';
import { clearOauthData } from 'redux/oauth/actions';
import { selectExistsAuthToken } from 'redux/auth/selectors';
import { logout } from 'redux/auth/actions';
import Icon from 'components/Icon';
import Button from 'components/ui/Button';

import css from './AuthLayout.module.scss';

const AuthLayout: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const dispatch = useAppDispatch();

    const authorized = useAppSelector(selectExistsAuthToken);
    const verified = useAppSelector(isEmailVerified);
    const account = useAppSelector(selectAccount);
    const settings = useAppSelector(selectAccountSettings);

    const accountStatus = account?.status;
    const location = useLocation();
    const isOnboarding = location.pathname.includes('onboarding');
    const isJoin = location.pathname === ROUTES.JOIN;
    const isAccountDeleted = accountStatus === 'deleted';

    const joinButtonAction = () => {
        dispatch(clearOauthData);
        navigate(ROUTES.LOGIN);
    };

    const onboardingButtonAction = () => dispatch(logout());

    if (authorized && !isOnboarding && verified && !isAccountDeleted && !settings) {
        navigate(ROUTES.HOME);
    }

    if (isOnboarding && !authorized) {
        navigate(ROUTES.LOGIN);
    }

    return (
        <div className={css.layoutContainer}>
            <div className={clsx(css.logoRow, (isOnboarding || isJoin) && css.withButton)}>
                <Link to={ROUTES.LANDING}>
                    <Icon name="interpals-logo" color="white" width={180} height={32} />
                </Link>
                {(isOnboarding || isJoin) && (
                    // Todo: Consider moving this into a menu that contains language selection, etc.
                    <Button
                        color="blue"
                        withPadding
                        className={css.button}
                        onClick={() => (isJoin ? joinButtonAction() : onboardingButtonAction())}
                    >
                        {isJoin ? t('Header.Home') : t('Onboarding.Signin.Signout')}
                    </Button>
                )}
            </div>
            <Outlet />
        </div>
    );
};

export default React.memo(AuthLayout);
